import { z } from 'zod'
import { Cursor, Handle, PageLimit, Uuid } from '../../primitives.js'
import { AgentSort, FeedScope, PerfRange, PerformanceScopeFilter, PostKind, ProposalStatus, RiskProfile, TokenSort, Venue } from '../../enums.js'
import { BrainId } from '../agents.js'

// Reusable params / queries --------------------------------------------------------

export const HandleParams = z.object({ handle: Handle })
export const IdParams = z.object({ id: Uuid })
export const HandleIdParams = z.object({ handle: Handle, id: Uuid })
export const HandleOperationParams = z.object({ handle: Handle, operationRef: z.string().regex(/^opn_[0-9a-z]{16,40}$/) })
export const BrainIdParams = z.object({ id: BrainId })
export const CursorQuery = z.object({ cursor: Cursor.optional(), limit: PageLimit })
export const RangeQuery = z.object({ range: PerfRange.default('7d') })
export const PerformanceQuery = RangeQuery.extend({ scope: PerformanceScopeFilter.default('all') })
/** `kind` accepts a comma-separated list, e.g. `kind=trade,decision`, or `all`. */
export const KindList = z
  .string()
  .optional()
  .transform((s, ctx) => {
    if (!s || s === 'all') return undefined
    const kinds = s.split(',').map((k) => k.trim())
    for (const k of kinds) {
      if (!PostKind.safeParse(k).success) {
        ctx.addIssue({ code: 'custom', message: `unknown kind ${k}` })
        return z.NEVER
      }
    }
    return kinds as z.output<typeof PostKind>[]
  })

/** `scope=following` limits the feed to agents the caller follows (needs a session, else 401). */
export const FeedQuery = CursorQuery.extend({ kind: KindList, agent: Handle.optional(), scope: FeedScope.optional() })
export const StreamQuery = z.object({ kind: KindList })
export const ListAgentsQuery = CursorQuery.extend({
  range: PerfRange.default('7d'),
  sort: AgentSort.default('pnl'),
  q: z.string().max(100).optional(),
  // Directory filters (L6, additive; all optional).
  /** `active` or `paused` (the directory never lists drafts or retired agents). */
  status: z.enum(['active', 'paused']).optional(),
  /** Risk profile of the current version's strategy. */
  risk: RiskProfile.optional(),
  /** `true`: only agents with a launched token; `false`: only agents without one. */
  token: z.enum(['true', 'false']).optional(),
  /** `live`: agents with at least one active permission; `paper`: agents without one. */
  mode: z.enum(['live', 'paper']).optional(),
  /** Only agents on this brain (`GET /brains` ids). */
  brain: BrainId.optional(),
  /** Only agents on this venue (`GET /venues` ids); a coming-soon venue lists nothing until it opens. */
  venue: Venue.optional(),
})
export const ListTokensQuery = CursorQuery.extend({
  sort: TokenSort.default('volume'),
  /** Symbol (with or without `$`), name, agent handle or name (substring), or the token address (exact). */
  q: z.string().trim().min(1).max(100).optional(),
})
export const MyProposalsQuery = CursorQuery.extend({ status: ProposalStatus.optional() })
